(() => {
  const money = cents => {
    if (window.theme && theme.Currency && theme.settings) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  };

  const updateProgress = total => {
    document.querySelectorAll('[data-onyra-cart-progress]').forEach(progress => {
      const threshold = Number(progress.dataset.threshold);
      const remaining = Math.max(threshold - total, 0);
      const percentage = threshold > 0 ? Math.min((total / threshold) * 100, 100) : 0;
      const message = progress.querySelector('[data-onyra-cart-progress-message]');
      const bar = progress.querySelector('[data-onyra-cart-progress-bar]');

      if (message) {
        message.textContent = remaining > 0
          ? progress.dataset.remainingTemplate.replace('[amount]', money(remaining))
          : progress.dataset.unlockedMessage;
      }

      if (bar) {
        bar.style.setProperty('--onyra-cart-progress', `${percentage}%`);
        bar.parentElement.setAttribute('aria-valuenow', String(Math.round(percentage)));
      }
    });
  };

  const initStickyAtc = () => {
    const sticky = document.querySelector('[data-onyra-sticky-atc]');
    const productSection = sticky?.closest('.shopify-section')?.querySelector('.product-section')
      || document.querySelector('.product-section');
    if (!sticky || !productSection || sticky.dataset.onyraStickyReady) return;

    const stickyButton = sticky.querySelector('[data-onyra-sticky-button]');
    const stickyPrice = sticky.querySelector('[data-onyra-sticky-price]');
    if (!stickyButton) return;
    sticky.dataset.onyraStickyReady = 'true';

    const getMainButton = () => productSection.querySelector('[data-add-to-cart]');
    let currentButton = null;
    let mainButtonVisible = true;
    let hiddenAfterAdd = false;
    let pendingSync = false;

    const updateVisibility = () => {
      const cartOpen = document.documentElement.classList.contains('js-drawer-open')
        || document.getElementById('CartDrawer')?.classList.contains('drawer--is-open');
      sticky.hidden = !currentButton || mainButtonVisible || hiddenAfterAdd || cartOpen;
    };

    const visibilityObserver = new IntersectionObserver(entries => {
      const entry = entries.find(item => item.target === currentButton);
      if (!entry) return;
      mainButtonVisible = entry.isIntersecting;
      if (mainButtonVisible) hiddenAfterAdd = false;
      updateVisibility();
    }, { threshold: 0.2 });

    // Shopify's variant refresh replaces the original [data-add-to-cart]
    // element. Always use the newest, connected button and price elements.
    const sync = () => {
      const button = getMainButton();
      if (!button) {
        stickyButton.disabled = true;
        updateVisibility();
        return;
      }

      stickyButton.disabled = button.disabled;
      stickyButton.textContent = button.querySelector('[data-add-to-cart-text]')?.textContent.trim()
        || button.textContent.trim();

      const mainPrice = productSection.querySelector('[data-product-price]');
      const mainComparePrice = productSection.querySelector('[data-compare-price]');
      const mainCompareWrap = mainComparePrice?.closest('[data-product-price-wrap]');
      if (!mainPrice || !stickyPrice) return;

      const currentPriceText = mainPrice.textContent.trim();
      const comparePriceText = mainComparePrice?.textContent.trim();
      const hasComparePrice = mainPrice.classList.contains('on-sale')
        && comparePriceText && !mainCompareWrap?.classList.contains('hide');

      if (!hasComparePrice) {
        if (stickyPrice.textContent !== currentPriceText || stickyPrice.children.length) {
          stickyPrice.textContent = currentPriceText;
        }
        return;
      }

      const existingCompare = stickyPrice.querySelector('s');
      const existingCurrent = stickyPrice.querySelector('span');
      if (existingCompare?.textContent === comparePriceText
          && existingCurrent?.textContent === currentPriceText) return;

      const comparePrice = document.createElement('s');
      comparePrice.textContent = comparePriceText;
      comparePrice.style.color = getComputedStyle(mainComparePrice).color;
      comparePrice.style.fontWeight = '400';
      comparePrice.style.opacity = '0.7';
      comparePrice.style.marginInlineEnd = '8px';

      const currentPrice = document.createElement('span');
      currentPrice.textContent = currentPriceText;
      currentPrice.style.color = getComputedStyle(mainPrice).color;
      currentPrice.style.fontWeight = '700';
      stickyPrice.replaceChildren(comparePrice, currentPrice);
    };

    const rebind = () => {
      const nextButton = getMainButton();
      if (nextButton !== currentButton) {
        if (currentButton) visibilityObserver.unobserve(currentButton);
        currentButton = nextButton;
        mainButtonVisible = true;
        if (currentButton) visibilityObserver.observe(currentButton);
      }
      sync();
      updateVisibility();
    };

    const scheduleSync = () => {
      if (pendingSync) return;
      pendingSync = true;
      requestAnimationFrame(() => {
        pendingSync = false;
        rebind();
      });
    };

    // Observe replacement of the button, inventory and price snippets.
    new MutationObserver(scheduleSync).observe(productSection, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'hidden', 'class']
    });

    const cartDrawer = document.getElementById('CartDrawer');
    if (cartDrawer) {
      new MutationObserver(updateVisibility).observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
    new MutationObserver(updateVisibility).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    document.addEventListener('drawerOpen.CartDrawer', updateVisibility);
    document.addEventListener('ajaxProduct:added', event => {
      if (!event.detail?.product) return;
      hiddenAfterAdd = true;
      updateVisibility();
    });
    document.addEventListener('kit:variant-picker:after-change', event => {
      if (productSection.contains(event.target)) scheduleSync();
    });

    stickyButton.addEventListener('click', () => {
      const liveButton = getMainButton();
      if (!liveButton || !liveButton.isConnected || liveButton.disabled) return;
      liveButton.click();
    });

    rebind();
  };

  const updateDispatchDates = (now = new Date()) => {
    const dates = document.querySelectorAll('[data-onyra-dispatch-date]');
    if (!dates.length) return;
    dates.forEach(node => {
      const dateParts = new Intl.DateTimeFormat('en-CA', {
        timeZone: node.dataset.timeZone || 'Europe/Athens', year: 'numeric', month: '2-digit', day: '2-digit'
      }).formatToParts(now);
      const getPart = type => dateParts.find(part => part.type === type).value;
      const dispatchDate = new Date(Date.UTC(Number(getPart('year')), Number(getPart('month')) - 1, Number(getPart('day')), 12));
      let days = 0;
      while (days < 2) {
        dispatchDate.setUTCDate(dispatchDate.getUTCDate() + 1);
        if (dispatchDate.getUTCDay() !== 0 && dispatchDate.getUTCDay() !== 6) days++;
      }
      const parts = new Intl.DateTimeFormat(node.lang || document.documentElement.lang || 'el', {
        timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long'
      }).formatToParts(dispatchDate);
      const part = type => parts.find(value => value.type === type).value;
      node.dateTime = dispatchDate.toISOString().slice(0, 10);
      node.textContent = `${part('weekday')}, ${part('day')} ${part('month')}`;
    });
  };

  const initProductEnhancements = () => {
    document.querySelectorAll('[data-onyra-product-confidence]').forEach(confidence => {
      const form = confidence.closest('form');
      const stockAlert = confidence.querySelector('[data-onyra-stock-alert]');
      if (!form || !stockAlert || form.dataset.onyraEnhancementsInitialized === 'true') return;

      const syncAvailability = () => {
        const button = form.querySelector('[data-add-to-cart]');
        const shouldHide = !button || button.disabled;
        if (stockAlert.hidden !== shouldHide) stockAlert.hidden = shouldHide;
      };

      form.dataset.onyraEnhancementsInitialized = 'true';
      new MutationObserver(syncAvailability).observe(form, {
        attributes: true,
        childList: true,
        subtree: true
      });
      syncAvailability();
    });
  };

  document.addEventListener('cart:updated', event => {
    if (event.detail && event.detail.cart) updateProgress(event.detail.cart.total_price);
  });

  document.addEventListener('DOMContentLoaded', () => {
    const cartProgress = document.querySelector('[data-onyra-cart-progress]');
    updateProgress(Number(cartProgress?.dataset.cartTotal || 0));
    initStickyAtc();
    initProductEnhancements();
    updateDispatchDates();
  });

  document.addEventListener('shopify:section:load', initProductEnhancements);
  document.addEventListener('shopify:section:load', () => updateDispatchDates());
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) updateDispatchDates();
  });
})();
